import { Component, h, primitiveNodeTypes, useRef, useState } from "@lukekaalim/act";
import { html } from "@lukekaalim/act-web";

import classes from '../Mini.module.css';

import { PropsForTypeOfLogicEditor } from "./common";
import { useFrontendServices } from "@kosmod/core";
import { ModalComponent, useAsync, useJeffect } from "@kosmod/kit";
import { LabeledInput } from "@kosmod/sesame";
import { MiniModel } from "../../../../defs";

import * as three from 'three';
import { a3, ThreeJSRoot } from "@lukekaalim/act-three";
import { Graphics3DSetup, ObjectClone, OrbitCamera, renderBeat } from "@kosmod/graphics-3d";
import { TheatreSceneResources } from "../../../../hooks";
import { GLTFLoader } from "three/examples/jsm/Addons.js";
import { first } from "rxjs";
import { MiniModelPreview } from "../../../Theatre/Mini/Preview";

export const RenderLogicEditor: Component<PropsForTypeOfLogicEditor<"render">> = ({
  content, logic, resources, onLogicChange,
  onLogicDataChange,

  graphics,
}) => {
  const { modal } = useFrontendServices();

  const is2D = logic.data.renderType === 'game-mat' || logic.data.renderType === 'quick-sprite';
  const { data } = logic;

  return [
    h(html.div, { className: classes.miniTemplateLogicButtonRow }, [
      h(html.button, { type: 'button', async onClick() {
        const fileInput = document.createElement('input');
        fileInput.type = "file";
        fileInput.click();
        const file = await new Promise<File | null>(r => {
          const onInput = () => {
            fileInput.removeEventListener('input', onInput);
            r(fileInput.files && fileInput.files.item(0))
          }
          fileInput.addEventListener('input', onInput);
        });
        if (!file)
          return;

        if (file.type.startsWith('image/')) {
          const asset = await content.assets.uploadBlob(file)
          const sprite = await content.createNewMiniSprite({ type: 'reference', id: asset })
          content.refresh();
          
          onLogicChange({ key: 'render', enabled: true, data: { renderType: 'quick-sprite', spriteId: sprite.id}})
        }
        if (file.type.startsWith('model/')) {
          const asset = await content.assets.uploadBlob(file)
          const path = await modal.add(EnterModalPathPopup(file));
          if (!path)
            return;

          const model = await content.createNewMiniModel({ type: 'reference', id: asset }, path)
          
          content.refresh();
          onLogicChange({ key: 'render', enabled: true, data: { renderType: 'model', modelId: model.id }})
        }
      } },
        'Upload Graphic (image or 3d model)'),
      h(html.button, { type: 'button', async onClick() {} },
        'Select Existing Graphic'),
    ]),
    is2D && h(html.select, { onChange() {
      if (data.renderType !== 'game-mat' && data.renderType !== 'quick-sprite')
        return;

      switch (this.value) {
        case 'quick-sprite':
          return onLogicDataChange({ renderType: 'quick-sprite', spriteId: data.spriteId })
        case 'game-mat':
          return onLogicDataChange({ renderType: 'game-mat', spriteId: data.spriteId, size: { x: 5, y: 5 } })
      }
    } }, [
      h(html.option, { selected: logic.data.renderType === 'quick-sprite', value: 'quick-sprite' }, '2D Sprite'),
      h(html.option, { selected: logic.data.renderType === 'game-mat', value: 'game-mat' }, 'Flat Map'),
    ]),
    data.renderType === 'game-mat' && h(html.div, { className: classes.vector2DInputContainer }, [
      h(html.input, { type: 'number', min: '0', valueAsNumber: data.size.x, onInput(event) {
        onLogicDataChange({ ...data, size: { ...data.size, x: this.valueAsNumber }})
      }, }),
      h(html.input, { type: 'number', min: '0', valueAsNumber: data.size.y, onInput(event) {
        onLogicDataChange({ ...data, size: { ...data.size, y: this.valueAsNumber }})
      }, }),
    ]),
      
    (data.renderType === 'quick-sprite' || data.renderType === 'game-mat') && [
      h(html.div, {}, `Mode: ${{ 'quick-sprite': '2D Sprite', 'game-mat': 'Flat Map' }[data.renderType]}`),
      data.spriteId && content.miniSprites.has(data.spriteId)
      ? [
        h(html.img, { src: resources.miniSpritesURLs.get(data.spriteId)?.href })
      ]
      : h(html.div, {}, `2D Sprite not found`)
    ],
    logic.data.renderType === 'model' && [
      h(html.div, {}, `Mode: 3D Model`),
      logic.data.modelId && h(MiniModelPreview, {
        graphics,
        miniModel: content.miniModels.get(logic.data.modelId) || null,
        resources,
        showBoundingBox: true,
      }),
      !logic.data.modelId && h(html.div, {}, `3D Model not found`)
    ]
  ]
}

const EnterModalPathPopup= (file: File): ModalComponent<string[]>  => ({ submit }) => {
  const [path, setPath] = useState('');

  const { result: gltf } = useAsync(async () => {
    const loader = new GLTFLoader();

    return loader.parseAsync(await file.arrayBuffer(), '.')
  }, [])

  const flattenObjectToPaths = (object: three.Object3D, basePath: string[] = []): string[] => {
    const path = [...basePath, object.name]
    return [
      path.join('.'),
      ...object.children.flatMap(child => flattenObjectToPaths(child, path))
    ]
  }

  return h(html.form, { onSubmit(event: SubmitEvent) {
    event.preventDefault();
    submit(path.split('.'))
  },}, [
    h(LabeledInput, {
      label: 'Enter model path, separated by \'.\'',
      name: 'gamename',
      placeholder: 'path.in.scene.to.mymodel',
      value: path,
      onInput(event) {
        setPath(this.value);
      },
    }),

    !!gltf && h(html.select, {
      onChange() {
        if (this.value)
          setPath(this.value);
      },
    }, [
      h(html.option, { value: '', selected: true }, 'none'),
      gltf.scene.children.flatMap(c => flattenObjectToPaths(c)).map(path => {
        return h(html.option, { value: path }, path)
      })
    ])
  ])
}
